import React, { useState, useEffect, useRef } from 'react';
import { createConnectSession, completeAccountLink } from '../services/api';

// v2 Mono Connect integration
const MONO_KEY = process.env.REACT_APP_MONO_PUBLIC_KEY || 'test_pk_ljmjqv693lbfcy2u8edx';

// Use globally loaded Mono Connect SDK
function useMonoConnect(onSuccessRef) {
  const monoRef = useRef(null);

  useEffect(() => {
    if (!MONO_KEY || !window.Connect) {
      console.log('[AfriLink] Mono SDK not available. MONO_KEY:', !!MONO_KEY, 'window.Connect:', !!window.Connect);
      return;
    }
    console.log('[AfriLink] Initializing Mono Connect with key:', MONO_KEY.substring(0, 15) + '...');
    monoRef.current = new window.Connect({
      key: MONO_KEY,
      scope: 'auth',
      data: {
        customer: {
          name: 'AfriLink User',
          email: 'user@afrilink.io',
        },
      },
      onSuccess: (data) => {
        console.log('[AfriLink] Mono success, code:', data.code);
        if (onSuccessRef.current) onSuccessRef.current(data.code);
      },
      onClose: () => {
        console.log('[AfriLink] Mono widget closed');
      },
    });
    monoRef.current.setup();
  }, [onSuccessRef]);

  return monoRef;
}

type Step = 'consent' | 'banks' | 'login' | 'linking' | 'success' | 'error';

const STEPS = ['Consent', 'Bank', 'Verify', 'Done'];

const BANKS = [
  { name: 'GTBank', code: 'GTB', color: '#E04403' },
  { name: 'Zenith Bank', code: 'ZEN', color: '#EC1C24' },
  { name: 'Access Bank', code: 'ACC', color: '#F68B1F' },
  { name: 'First Bank', code: 'FBN', color: '#003B71' },
  { name: 'UBA', code: 'UBA', color: '#CC0000' },
  { name: 'Kuda Bank', code: 'KDA', color: '#6C3AE0' },
  { name: 'Stanbic IBTC', code: 'STB', color: '#004B8D' },
  { name: 'FCMB', code: 'FCM', color: '#6A0DAD' },
];


const ProgressBar = ({ currentStep }) => (
  <div className="flex items-center justify-between px-2 mb-6">
    {STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <div className="flex flex-col items-center gap-1.5">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
              i < currentStep
                ? 'bg-green-500 text-white'
                : i === currentStep
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {i < currentStep ? '\u2713' : i + 1}
          </div>
          <span
            className={`text-[10px] font-medium ${
              i <= currentStep ? 'text-gray-700' : 'text-gray-300'
            }`}
          >
            {label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div
            className={`flex-1 h-0.5 mx-1 mt-[-14px] rounded ${
              i < currentStep ? 'bg-green-500' : 'bg-gray-100'
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

const ConnectWidget = ({
  userId = 'user_demo_001',
  onSuccess,
  onClose,
}) => {
  const [step, setStep] = useState('consent');
  const [selectedBank, setSelectedBank] = useState('');
  const [sessionId, setSessionId] = useState('');
  const sessionIdRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkedData, setLinkedData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const useMono = !!MONO_KEY;
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Mono Connect hook
  const handleMonoSuccess = async (authCode) => {
    console.log('[AfriLink] Mono auth code received:', authCode);
    console.log('[AfriLink] Session ID:', sessionIdRef.current);
    setStep('linking');
    setSelectedBank('Your Bank');
    try {
      const data = await completeAccountLink(sessionIdRef.current, userId, 'mono', authCode);
      if (data.success) {
        setLinkedData(data.account);
        setTimeout(() => {
          setStep('success');
          onSuccess?.(data.account);
        }, 1500);
      } else {
        setStep('error');
        setError('Failed to link account');
      }
    } catch (err) {
      setStep('error');
      setError('Connection failed. Please try again.');
    }
  };

  const monoSuccessRef = useRef(handleMonoSuccess);
  useEffect(() => { monoSuccessRef.current = handleMonoSuccess; });
  const monoRef = useMonoConnect(monoSuccessRef);

  const currentStepIndex =
    step === 'consent' ? 0 : step === 'banks' ? 1 : step === 'login' ? 2 : 3;

  const handleConsent = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await createConnectSession(userId);
      if (data.success) {
        setSessionId(data.sessionId);

        // If Mono is configured, open Mono Connect widget directly
        if (useMono && monoRef.current) {
          setLoading(false);
          monoRef.current.open();
          return;
        }

        // Otherwise fall back to mock bank selection
        setStep('banks');
      } else {
        setError('Failed to start session');
      }
    } catch (err) {
      setError('Cannot connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleBankSelect = (bankName) => {
    setSelectedBank(bankName);
    setError('');
    setStep('login');
  };

  const handleLogin = async () => {
    setLoading(true);
    setStep('linking');
    setError('');
    try {
      const data = await completeAccountLink(sessionId, userId, selectedBank);
      if (data.success) {
        setLinkedData(data.account);
        setTimeout(() => {
          setStep('success');
          onSuccess?.(data.account);
        }, 1500);
      } else {
        setStep('error');
        setError('Failed to link account');
      }
    } catch (err) {
      setStep('error');
      setError('Connection failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('consent');
    setSelectedBank('');
    setSessionId('');
    setError('');
    setLinkedData(null);
    setSearchQuery('');
  };

  const filteredBanks = BANKS.filter((b) =>
    b.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="widget-container bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-lg">
      {/* Header */}
      <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 bg-black rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">AL</span>
          </div>
          <span className="text-sm font-semibold text-gray-900">AfriLink Connect</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-300 hover:text-gray-500 transition">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4 4L14 14M14 4L4 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      <div className="px-6 pt-5 pb-6">
        {step !== 'linking' && <ProgressBar currentStep={currentStepIndex} />}

        {/* Consent */}
        {step === 'consent' && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Connect your bank account</h2>
            <p className="text-sm text-gray-500 mb-5">
              AfriLink will securely access the following with your permission:
            </p>
            <div className="space-y-3 mb-6">
              {[
                { icon: 'account', label: 'Account details', desc: 'Name, type, and number' },
                { icon: 'balance', label: 'Account balance', desc: 'Current and available balance' },
                { icon: 'txn', label: 'Transaction history', desc: 'Up to 12 months of data' },
              ].map((item) => (
                <div key={item.icon} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 bg-white rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8L6.5 11.5L13 4.5" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.label}</p>
                    <p className="text-xs text-gray-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mb-4">
              Your credentials are encrypted end-to-end and never stored by AfriLink. You can revoke access at any time.
            </p>
            <button
              onClick={handleConsent}
              disabled={loading}
              className="w-full py-3.5 bg-black text-white rounded-2xl font-semibold text-sm hover:bg-gray-800 disabled:opacity-60 transition"
            >
              {loading ? 'Starting session...' : 'Agree and continue'}
            </button>
            {error && <p className="text-red-500 text-xs mt-3 text-center">{error}</p>}
          </div>
        )}

        {/* Bank selection */}
        {step === 'banks' && (
          <div>
            <button onClick={() => setStep('consent')} className="text-gray-400 hover:text-gray-600 text-xs mb-3 flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/></svg>
              Back
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Select your bank</h2>
            <input
              type="text"
              placeholder="Search banks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm mb-4 outline-none focus:border-gray-400 transition"
            />
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredBanks.map((bank) => (
                <button
                  key={bank.code}
                  onClick={() => handleBankSelect(bank.name)}
                  className="w-full flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:bg-gray-50 transition text-left"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                    style={{ backgroundColor: bank.color }}
                  >
                    {bank.code}
                  </div>
                  <span className="text-sm font-medium text-gray-900">{bank.name}</span>
                  <svg className="ml-auto text-gray-300" width="14" height="14" viewBox="0 0 14 14"><path d="M5 2.5L9.5 7L5 11.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/></svg>
                </button>
              ))}
              {filteredBanks.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No banks match your search</p>
              )}
            </div>
          </div>
        )}

        {/* Login */}
        {step === 'login' && (
          <div>
            <button onClick={() => setStep('banks')} className="text-gray-400 hover:text-gray-600 text-xs mb-3 flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/></svg>
              Back
            </button>
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-base font-bold mx-auto mb-4"
                style={{ backgroundColor: BANKS.find((b) => b.name === selectedBank)?.color || '#000' }}
              >
                {BANKS.find((b) => b.name === selectedBank)?.code || '?'}
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Log in to {selectedBank}</h2>
              <p className="text-xs text-gray-400 mb-6">Secure 256-bit encrypted connection</p>
            </div>
            <div className="space-y-3 mb-6">
              <input
                type="text"
                placeholder="Account ID or Username"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
                defaultValue="demo_user"
              />
              <input
                type="password"
                placeholder="Password"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400 transition"
                defaultValue="demo1234"
              />
            </div>
            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full py-3.5 bg-black text-white rounded-2xl font-semibold text-sm hover:bg-gray-800 disabled:opacity-60 transition"
            >
              Log in securely
            </button>
            <p className="text-[11px] text-gray-400 text-center mt-4">
              AfriLink never stores your bank credentials
            </p>
            {error && <p className="text-red-500 text-xs mt-3 text-center">{error}</p>}
          </div>
        )}

        {/* Linking animation */}
        {step === 'linking' && (
          <div className="py-12 text-center">
            <div className="relative mx-auto w-16 h-16 mb-6">
              <div className="absolute inset-0 rounded-full border-2 border-gray-100" />
              <div className="absolute inset-0 rounded-full border-2 border-black border-t-transparent animate-spin" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Linking your account...</h2>
            <p className="text-sm text-gray-400">Securely connecting to {selectedBank}</p>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M7 14.5L11.5 19L21 9" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Account connected</h2>
            <p className="text-sm text-gray-500 mb-6">{selectedBank} has been linked successfully</p>
            {linkedData && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Account</span>
                  <span className="text-gray-900 font-medium">{linkedData.accountName}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Balance</span>
                  <span className="text-gray-900 font-mono">
                    {linkedData.currency} {linkedData.balance?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Institution</span>
                  <span className="text-gray-900">{linkedData.institution}</span>
                </div>
              </div>
            )}
            <button
              onClick={onClose || reset}
              className="w-full py-3.5 bg-black text-white rounded-2xl font-semibold text-sm hover:bg-gray-800 transition"
            >
              Done
            </button>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 7L17 17M17 7L7 17" stroke="#E24B4A" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Connection failed</h2>
            <p className="text-sm text-gray-500 mb-6">{error || 'Something went wrong'}</p>
            <button
              onClick={reset}
              className="w-full py-3.5 bg-black text-white rounded-2xl font-semibold text-sm hover:bg-gray-800 transition"
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-center gap-1.5">
        <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
          <rect x="1" y="5" width="8" height="6" rx="1.5" stroke="#9CA3AF" strokeWidth="1" />
          <path d="M3 5V3.5C3 2.12 4.12 1 5.5 1h0C6.88 1 8 2.12 8 3.5V5" stroke="#9CA3AF" strokeWidth="1" strokeLinecap="round" fill="none" />
        </svg>
        <span className="text-[10px] text-gray-400">Secured by AfriLink</span>
      </div>
    </div>
  );
};

export default ConnectWidget;
